import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { logger } from "../lib/logger";

const NotificationContext = createContext(null);
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WS_BASE = BACKEND_URL?.replace(/^http/, "ws") || "";

// Short soft chime played on new chat messages (Web Audio, no asset files)
const playMessageSound = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = "sine";
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(1320, ctx.currentTime + 0.12);
    gain.gain.setValueAtTime(0.12, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.45);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.5);
    setTimeout(() => { try { ctx.close(); } catch (err) { logger.debug("audio ctx close:", err); } }, 700);
  } catch (err) { logger.debug("audio unavailable:", err); }
};

// Map a notification to its in-app destination
export const notifTarget = (n, role) => {
  if (!n?.type) return null;
  if (role === "admin") {
    if (n.type === "name_change_request") return "/admin/name-requests";
    if (n.type === "new_registration") return "/admin";
    if (n.type === "trader_message") return n.merchant_id ? `/admin/messages?merchant=${n.merchant_id}` : "/admin/messages";
  if (n.type === "whatsapp_incoming" || n.type === "whatsapp_instance") return n.conversation_id ? `/admin/whatsapp?c=${n.conversation_id}` : "/admin/whatsapp";
    return null;
  }
  if (role === "employee") {
    if (n.type === "leave_decision") return "/employee/leaves";
    if (n.type === "public_order_new") return n.order_id ? `/store-orders?o=${n.order_id}` : "/store-orders";
    if (["employee_sale_new", "employee_sale_status", "employee_purchase_status", "employee_store_update", "reservation_new", "reservation_status"].includes(n.type)) return n.reservation_id ? `/reservations?r=${n.reservation_id}` : "/reservations";
    if (n.type === "message_new") return n.reservation_id ? `/messages/${n.reservation_id}` : "/messages";
    return null;
  }
  switch (n.type) {
    case "leave_request":
      return "/employees";
    case "public_order_new":
      return n.order_id ? `/store-orders?o=${n.order_id}` : "/store-orders";
    case "message_new":
      return n.reservation_id ? `/messages/${n.reservation_id}` : "/messages";
    case "admin_message":
      return "/contact-admin";
    case "reservation_new":
    case "reservation_status":
      return "/reservations";
    case "rating_new":
    case "name_change_result":
      return "/profile";
    case "market_shortage":
      return "/upload";
    case "low_stock":
    case "out_of_stock":
      return n.product_id ? `/my-photos?highlight=${n.product_id}` : "/my-photos";
    case "supplier_reply":
    case "gmail_reconnect":
    case "sourcing_digest":
      return n.conversation_id ? `/messages/external-sourcing?c=${n.conversation_id}` : "/messages/external-sourcing";
    default:
      return null;
  }
};

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [unreadReservations, setUnreadReservations] = useState(0);
  const [unreadTraderMessages, setUnreadTraderMessages] = useState(0);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/notifications");
      setItems(r.data.items || []);
      setUnread(r.data.unread || 0);
      setUnreadMessages(r.data.unread_messages || 0);
      setUnreadReservations(r.data.unread_reservations || 0);
      setUnreadTraderMessages(r.data.unread_trader_messages || 0);
    } catch (err) { logger.warn("notif fetch:", err); }
  }, []);

  const markAllRead = useCallback(async () => {
    try {
      await api.post("/notifications/read-all");
      setUnread(0);
      setUnreadMessages(0);
      setUnreadReservations(0);
      setUnreadTraderMessages(0);
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch (err) { logger.warn("mark read:", err); }
  }, []);

  const markMessagesRead = useCallback(async () => {
    try {
      await api.post("/notifications/read-messages");
      setUnreadMessages((m) => {
        setUnread((u) => Math.max(0, u - m));
        return 0;
      });
      setItems((prev) => prev.map((n) => (n.type === "message_new" ? { ...n, read: true } : n)));
    } catch (err) { logger.warn("mark msgs read:", err); }
  }, []);

  const markReservationsRead = useCallback(async () => {
    try {
      await api.post("/notifications/read-reservations");
      setUnreadReservations((c) => {
        setUnread((u) => Math.max(0, u - c));
        return 0;
      });
      setItems((prev) => prev.map((n) => (
        n.type === "reservation_new" || n.type === "reservation_status" ? { ...n, read: true } : n
      )));
    } catch (err) { logger.warn("mark res read:", err); }
  }, []);

  const openNotification = useCallback((n) => {
    const target = notifTarget(n, user?.role);
    if (target) navigate(target);
  }, [user?.role, navigate]);

  useEffect(() => {
    if (!user) {
      setItems([]); setUnread(0); setUnreadMessages(0); setUnreadReservations(0); setUnreadTraderMessages(0);
      if (wsRef.current) { try { wsRef.current.close(); } catch (err) { logger.debug("ws close:", err); } wsRef.current = null; }
      return;
    }
    refresh();

    const token = localStorage.getItem("imdad_token");
    if (!token) return;
    const ws = new WebSocket(`${WS_BASE}/api/ws/notifications?token=${encodeURIComponent(token)}`);
    wsRef.current = ws;
    ws.onopen = () => setConnected(true);
    ws.onclose = () => setConnected(false);
    ws.onerror = (e) => { setConnected(false); logger.warn("notif ws err:", e); };
    ws.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data);
        if (payload.type === "connected") return;
        if (payload.type === "message_new") playMessageSound();
        if (payload.type === "supplier_reply") window.dispatchEvent(new CustomEvent("imdad:supplier_reply", { detail: payload }));
        const target = notifTarget(payload, user?.role);
        toast(payload.title || "إشعار جديد", {
          description: payload.message,
          ...(target ? { action: { label: "فتح", onClick: () => navigate(target) } } : {}),
        });
        setItems((prev) => [{ ...payload, read: false, id: payload.id || `${Date.now()}` }, ...prev].slice(0, 100));
        setUnread((u) => u + 1);
        if (payload.type === "message_new") setUnreadMessages((m) => m + 1);
        if (payload.type === "trader_message") setUnreadTraderMessages((m) => m + 1);
        if (payload.type === "reservation_new" || payload.type === "reservation_status") {
          setUnreadReservations((c) => c + 1);
        }
      } catch (err) { logger.warn("parse notif:", err); }
    };

    const ping = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) ws.send("ping");
      refresh();
    }, 30000);

    return () => {
      clearInterval(ping);
      try { ws.close(); } catch (err) { logger.debug("ws cleanup close:", err); }
      wsRef.current = null;
    };
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const actions = useMemo(
    () => ({ refresh, markAllRead, markMessagesRead, markReservationsRead, openNotification }),
    [refresh, markAllRead, markMessagesRead, markReservationsRead, openNotification]
  );
  const value = useMemo(
    () => ({ items, unread, unreadMessages, unreadReservations, unreadTraderMessages, connected, ...actions }),
    [items, unread, unreadMessages, unreadReservations, unreadTraderMessages, connected, actions]
  );
  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
};

export const useNotifications = () => useContext(NotificationContext) || {
  items: [], unread: 0, unreadMessages: 0, unreadReservations: 0, unreadTraderMessages: 0, connected: false,
  refresh: () => {}, markAllRead: () => {}, markMessagesRead: () => {}, markReservationsRead: () => {}, openNotification: () => {},
};
